import React, { useEffect, useState } from 'react';
import { Loader, Modal } from 'semantic-ui-react';
import styled from '@emotion/styled';
import Button from './Button';

const PreviewList = styled.div`
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 0 4px 4px;
`;

const ReviewNavigation = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dfe5ed;
  border-radius: 10px;
  bottom: 4px;
  box-shadow: 0 4px 14px rgba(32, 48, 72, 0.12);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 0 4px;
  padding: 10px 12px;
  position: sticky;
  z-index: 3;

  @media (max-width: 700px) {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
`;

const ReviewNavigationStatus = styled.div`
  color: #252a31;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
`;

const ReviewNavigationHint = styled.div`
  color: #788392;
  font-size: 13px;
  font-weight: 400;
`;

const HeaderCompressionSummary = styled.div`
  background: #e9f8ef;
  border-radius: 999px;
  color: #27824c;
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  margin-top: 8px;
  padding: 6px 10px;
`;

const ReviewNavigationButtons = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 700px) {
    button {
      flex: 1 1 0;
    }
  }
`;

const DocumentReview = styled.div`
  background: #ffffff;
  border: 1px solid #e1e6ee;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(32, 48, 72, 0.06);
  margin-bottom: 20px;
  padding: 20px;
`;

const HeaderContent = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
`;

const HeaderBadge = styled.div`
  align-items: center;
  background: #fff0ef;
  border: 1px solid #ffd2cf;
  border-radius: 10px;
  color: #ff5252;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  height: 42px;
  justify-content: center;
  letter-spacing: 0.04em;
  width: 42px;
`;

const HeaderTitle = styled.div`
  color: #252a31;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
`;

const HeaderSubtitle = styled.div`
  color: #697383;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  margin-top: 4px;
  max-width: 980px;
`;

const ActiveDocumentHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const DocumentIdentity = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
`;

const FileBadge = styled.div`
  align-items: center;
  background: #f1f4f8;
  border-radius: 7px;
  color: #647184;
  display: flex;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  height: 30px;
  justify-content: center;
  letter-spacing: 0.03em;
  width: 30px;
`;

const DocumentName = styled.div`
  color: #252a31;
  font-size: 16px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocumentSubtext = styled.div`
  color: #8993a2;
  font-size: 12px;
  margin-top: 3px;
`;

const SizeSummary = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

const SizeValue = styled.span`
  color: #566171;
  font-size: 14px;
  font-weight: 600;
`;

const SizeArrow = styled.span`
  color: #a1aab7;
  font-size: 16px;
`;

const SavingsBadge = styled.span`
  background: #e9f8ef;
  border-radius: 999px;
  color: #27824c;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
`;

const PreviewColumns = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewPanel = styled.div`
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px;
`;

const PreviewLabel = styled.div`
  align-items: center;
  color: #4d4e4d;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 7px;
  margin-bottom: 10px;
`;

const PreviewLabelDot = styled.span`
  background: #ff5252;
  border-radius: 50%;
  display: inline-block;
  height: 7px;
  width: 7px;
`;

const PreviewFrame = styled.iframe`
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 7px;
  box-shadow: 0 2px 7px rgba(32, 48, 72, 0.08);
  height: 560px;
  width: 100%;
`;

const NoReductionState = styled.div`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e1e6ee;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 36px 32px;
  text-align: center;
`;

const NoReductionStateTitle = styled.div`
  color: #252a31;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
`;

const NoReductionStateText = styled.div`
  color: #697383;
  font-size: 14px;
  line-height: 1.55;
  max-width: 560px;
`;

const NoReductionPreview = styled(PreviewPanel)`
  margin-top: 8px;
  max-width: 900px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  margin: 16px 0;
  white-space: pre-wrap;
`;

const SavingOverlay = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.94);
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  color: #4d4e4d;
  text-align: center;
  z-index: 10;
`;

const ReviewLoader = styled(Loader)`
  && {
    color: #ff5252 !important;
  }

  &&:before {
    border-color: rgba(255, 82, 82, 0.2) !important;
  }

  &&:after {
    border-color: #ff5252 transparent transparent !important;
  }
`;

const ActionBar = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;

  @media (max-width: 900px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const FooterNote = styled.div`
  color: #788392;
  font-size: 12px;
  line-height: 1.4;
  max-width: 320px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-end;

  @media (max-width: 700px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const formatFileSize = (sizeBytes: number) => {
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
};

export interface CompressionPreview {
  artifactId: string;
  documentId: number;
  fileName: string;
  originalDocumentUrl: string;
  compressedDocumentUrl: string;
  originalSizeBytes: number;
  compressedSizeBytes: number;
  reductionPercentage: number;
  isSmaller: boolean;
}

interface Props {
  open: boolean;
  previews: CompressionPreview[];
  onClose: () => void;
  onSendOriginal: () => void;
  onConfirm: (artifactIds: string[]) => Promise<void> | void;
  sendOriginalButtonText?: string;
  confirmButtonText?: string;
}

const PdfCompressionReviewModal = (props: Props) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeDocumentId, setActiveDocumentId] = useState<number | null>(null);
  const [reviewedDocumentIds, setReviewedDocumentIds] = useState<number[]>([]);

  useEffect(() => {
    if (props.open) {
      setSaving(false);
      setError('');
      setActiveDocumentId(props.previews[0]?.documentId || null);
      setReviewedDocumentIds(props.previews[0] ? [props.previews[0].documentId] : []);
    }
  }, [props.open, props.previews]);

  const smallerPreviews = props.previews.filter(preview => preview.isSmaller);
  const noReductionPreviews = props.previews.filter(preview => !preview.isSmaller);
  const onlyNoReduction = props.previews.length > 0 && smallerPreviews.length === 0;
  const showReviewNavigation = props.previews.length > 1 && !onlyNoReduction;
  const activePreview = props.previews.find(preview => preview.documentId === activeDocumentId);
  const activePreviewIndex = props.previews.findIndex(preview => preview.documentId === activeDocumentId);
  const reviewedCompressedCount = smallerPreviews.filter(preview =>
    reviewedDocumentIds.includes(preview.documentId)
  ).length;
  const activeNoReductionPreview =
    activePreview && !activePreview.isSmaller
      ? activePreview
      : props.previews.find(preview => !preview.isSmaller);
  const pdfLabel = props.previews.length === 1 ? 'PDF' : 'PDFs';

  const noReductionPreviewContent = activeNoReductionPreview ? (
    <NoReductionPreview>
      <PreviewLabel>
        <PreviewLabelDot />
        Original PDF
      </PreviewLabel>
      <PreviewFrame
        src={activeNoReductionPreview.originalDocumentUrl}
        title={`${activeNoReductionPreview.fileName} original`}
      />
    </NoReductionPreview>
  ) : null;

  const selectDocument = (documentId: number) => {
    setActiveDocumentId(documentId);
    setReviewedDocumentIds(currentIds =>
      currentIds.includes(documentId) ? currentIds : [...currentIds, documentId]
    );
  };

  const selectAdjacentDocument = (offset: number) => {
    const nextIndex = activePreviewIndex + offset;
    if (nextIndex >= 0 && nextIndex < props.previews.length) {
      selectDocument(props.previews[nextIndex].documentId);
    }
  };

  const handleConfirm = async () => {
    if (smallerPreviews.length === 0) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await props.onConfirm(smallerPreviews.map(preview => preview.artifactId));
    } catch (exception) {
      setSaving(false);
      setError(
        exception?.response?.data?.message ||
          'The compressed PDFs could not be saved. The original files are unchanged.'
      );
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      closeOnDimmerClick={false}
      style={{
        borderRadius: 14,
        maxWidth: 1600,
        overflow: 'hidden',
        padding: 0,
        width: onlyNoReduction ? '92%' : '96%',
        ...(onlyNoReduction ? { maxWidth: 640 } : {}),
      }}
    >
      <Modal.Header style={{ background: '#ffffff', padding: '22px 28px' }}>
        <HeaderContent>
          <HeaderBadge>PDF</HeaderBadge>
          <div>
            <HeaderTitle>
              {onlyNoReduction ? `The ${pdfLabel} Could Not Be Compressed` : 'Review compressed PDFs'}
            </HeaderTitle>
            {!onlyNoReduction && (
              <>
                <HeaderSubtitle>
                  If you confirm, the compressed PDFs will be used in this email and saved as new files on the
                  order. The original PDFs will remain unchanged.
                </HeaderSubtitle>
                <HeaderCompressionSummary>
                  {smallerPreviews.length} of {props.previews.length} {pdfLabel}{' '}
                  {props.previews.length === 1 ? 'has' : 'have'} been compressed.
                </HeaderCompressionSummary>
              </>
            )}
          </div>
        </HeaderContent>
      </Modal.Header>
      <Modal.Content style={{ background: '#f4f6f9', padding: '24px 28px' }}>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {onlyNoReduction ? (
          <NoReductionState>
            <NoReductionStateTitle>
              Compressing the {pdfLabel === 'PDF' ? 'file' : 'files'} did not produce{' '}
              {pdfLabel === 'PDF' ? 'a smaller version.' : 'smaller versions.'}
            </NoReductionStateTitle>
            <NoReductionStateText>
              The original {pdfLabel} remain{pdfLabel === 'PDF' ? 's' : ''} unchanged and will be used in this
              email. No new {pdfLabel === 'PDF' ? 'file was' : 'files were'} saved to the order.
            </NoReductionStateText>
            {noReductionPreviewContent}
          </NoReductionState>
        ) : (
          <PreviewList>
            {activePreview?.isSmaller ? (
              <DocumentReview>
                <ActiveDocumentHeader>
                  <DocumentIdentity>
                    <FileBadge>PDF</FileBadge>
                    <div style={{ minWidth: 0 }}>
                      <DocumentName title={activePreview.fileName}>{activePreview.fileName}</DocumentName>
                      <DocumentSubtext>Compressed file ready for approval</DocumentSubtext>
                    </div>
                  </DocumentIdentity>
                  <SizeSummary>
                    <SizeValue>{formatFileSize(activePreview.originalSizeBytes)}</SizeValue>
                    <SizeArrow>→</SizeArrow>
                    <SizeValue>{formatFileSize(activePreview.compressedSizeBytes)}</SizeValue>
                    <SavingsBadge>{activePreview.reductionPercentage}% smaller</SavingsBadge>
                  </SizeSummary>
                </ActiveDocumentHeader>
                <PreviewColumns>
                  <PreviewPanel>
                    <PreviewLabel>
                      <PreviewLabelDot />
                      Original PDF
                    </PreviewLabel>
                    <PreviewFrame
                      src={activePreview.originalDocumentUrl}
                      title={`${activePreview.fileName} original`}
                    />
                  </PreviewPanel>
                  <PreviewPanel>
                    <PreviewLabel>
                      <PreviewLabelDot />
                      Compressed PDF
                    </PreviewLabel>
                    <PreviewFrame
                      src={activePreview.compressedDocumentUrl}
                      title={`${activePreview.fileName} compressed`}
                    />
                  </PreviewPanel>
                </PreviewColumns>
              </DocumentReview>
            ) : (
              <NoReductionState>
                <NoReductionStateTitle>Compression did not make this PDF smaller.</NoReductionStateTitle>
                <NoReductionStateText>
                  The original PDF will be kept unchanged and used in this email. No new file will be added to
                  the order.
                </NoReductionStateText>
                {noReductionPreviewContent}
              </NoReductionState>
            )}
            {showReviewNavigation && (
              <ReviewNavigation>
                <div>
                  <ReviewNavigationStatus>
                    PDF {activePreviewIndex >= 0 ? activePreviewIndex + 1 : 1} of {props.previews.length}
                  </ReviewNavigationStatus>
                  <ReviewNavigationHint>
                    {reviewedCompressedCount} of {smallerPreviews.length} compressed PDFs reviewed.
                  </ReviewNavigationHint>
                </div>
                <ReviewNavigationButtons>
                  <Button
                    type="button"
                    secondary
                    onClick={() => selectAdjacentDocument(-1)}
                    disabled={activePreviewIndex <= 0}
                    overRidestyle={{ minWidth: 118 }}
                  >
                    Previous PDF
                  </Button>
                  <Button
                    type="button"
                    onClick={() => selectAdjacentDocument(1)}
                    disabled={activePreviewIndex < 0 || activePreviewIndex >= props.previews.length - 1}
                    overRidestyle={{ minWidth: 118 }}
                  >
                    Next PDF
                  </Button>
                </ReviewNavigationButtons>
              </ReviewNavigation>
            )}
          </PreviewList>
        )}
      </Modal.Content>
      <Modal.Actions style={{ background: '#ffffff', borderTop: '1px solid #e5e9ef', padding: '18px 28px' }}>
        <ActionBar style={onlyNoReduction ? { justifyContent: 'center' } : undefined}>
          {!onlyNoReduction && (
            <FooterNote>
              {reviewedCompressedCount < smallerPreviews.length
                ? `Review all ${smallerPreviews.length} compressed PDFs before saving. ${reviewedCompressedCount} of ${smallerPreviews.length} reviewed.`
                : noReductionPreviews.length > 0
                ? `${smallerPreviews.length} compressed PDF${
                    smallerPreviews.length === 1 ? '' : 's'
                  } ready to use. ${noReductionPreviews.length} original${
                    noReductionPreviews.length === 1 ? '' : 's'
                  } will be retained.`
                : `${smallerPreviews.length} compressed PDF${
                    smallerPreviews.length === 1 ? '' : 's'
                  } ready to use.`}
            </FooterNote>
          )}
          <Actions style={onlyNoReduction ? { justifyContent: 'center' } : undefined}>
            {onlyNoReduction ? (
              <Button
                type="button"
                secondary
                onClick={props.onSendOriginal}
                disabled={saving}
                overRidestyle={{ minWidth: 120 }}
              >
                OK
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  secondary
                  onClick={props.onSendOriginal}
                  disabled={saving}
                  overRidestyle={{ minWidth: 270 }}
                >
                  {props.sendOriginalButtonText || 'Send Original'}
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={
                    saving || smallerPreviews.length === 0 || reviewedCompressedCount < smallerPreviews.length
                  }
                  loadingStatus={saving}
                  overRidestyle={{ minWidth: 250 }}
                >
                  {props.confirmButtonText || 'Save Compressed & Send'}
                </Button>
              </>
            )}
          </Actions>
        </ActionBar>
      </Modal.Actions>
      {saving && (
        <SavingOverlay>
          <ReviewLoader active inline size="large" />
          <strong>Saving compressed PDFs and preparing the email…</strong>
          <span>Please wait while the confirmed files are saved.</span>
        </SavingOverlay>
      )}
    </Modal>
  );
};

export default PdfCompressionReviewModal;
